<template>
  <div class="device-management">
    <!-- 管理员视图：表格 + 批量导入 -->
    <template v-if="isAdmin">
      <!-- 搜索和操作栏 -->
      <el-row :gutter="20" style="margin-bottom: 20px;">
        <el-col :span="6">
          <el-input
            v-model="searchQuery"
            placeholder="请输入设备名称或ID"
            clearable
            @keyup.enter="handleSearch"
          >
            <template #append>
              <el-button type="primary" @click="handleSearch">搜索</el-button>
            </template>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="statusFilter"
            placeholder="筛选状态"
            clearable
            @change="handleStatusFilterChange"
            style="width: 100%"
          >
            <el-option label="全部" value="" />
            <el-option label="未激活" value="inactive" />
            <el-option label="已激活" value="active" />
            <el-option label="已禁用" value="disabled" />
            <el-option label="已删除" value="deleted" />
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-button type="success" @click="showEditDialog = true; resetForm()">注册设备</el-button>
        </el-col>
        <el-col :span="8">
          <el-button @click="triggerImport" :loading="importing">批量导入</el-button>
          <el-button type="info" @click="downloadTemplate" link>
            <el-icon><Download /></el-icon>
            下载模板
          </el-button>
          <input
            ref="importFileInput"
            type="file"
            accept=".csv"
            style="display: none"
            @change="handleFileChange"
          />
        </el-col>
      </el-row>

      <!-- 设备列表（管理员表格视图） -->
      <el-table
        :data="devices"
        style="width: 100%"
        v-loading="loading"
        :header-cell-style="{ background: '#f5f7fa' }"
      >
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column prop="device_id" label="设备ID" min-width="130" show-overflow-tooltip />
        <el-table-column prop="ble_identity" label="BLE标识" min-width="140" show-overflow-tooltip />
        <el-table-column prop="name" label="设备名称" min-width="100" show-overflow-tooltip />
        <el-table-column prop="board_type" label="类型" width="80">
          <template #default="scope">
            <el-tag v-if="scope.row.board_type === '1'" type="primary">{{ getBoardTypeName('1') }}</el-tag>
            <el-tag v-else-if="scope.row.board_type === '2'" type="success">{{ getBoardTypeName('2') }}</el-tag>
            <el-tag v-else-if="scope.row.board_type === '3'" type="warning">{{ getBoardTypeName('3') }}</el-tag>
            <span v-else>{{ getBoardTypeName(scope.row.board_type) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="user_name" label="所属用户" width="100" show-overflow-tooltip />
        <el-table-column prop="created_at" label="注册时间" width="140" />
        <el-table-column prop="status" label="状态" width="80">
          <template #default="scope">
            <el-tag v-if="scope.row.status === 'active'" type="success">已激活</el-tag>
            <el-tag v-else-if="scope.row.status === 'inactive'" type="info">未激活</el-tag>
            <el-tag v-else-if="scope.row.status === 'disabled'" type="warning">已禁用</el-tag>
            <el-tag v-else-if="scope.row.status === 'deleted'" type="danger">已删除</el-tag>
            <span v-else>{{ scope.row.status || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="二维码" width="120" align="center">
          <template #default="scope">
            <div v-if="scope.row.qrcodeUrl || scope.row.qrcode_url" class="qrcode-cell">
              <el-image
                :src="getQrcodeImageUrl(scope.row.qrcodeUrl || scope.row.qrcode_url)"
                :preview-src-list="[]"
                :preview-disabled="true"
                fit="cover"
                style="width: 60px; height: 60px; cursor: pointer;"
                :lazy="true"
                @click="onViewQrcode(scope.row)"
                @error="handleQrcodeImageError"
              >
                <template #error>
                  <div class="image-slot" style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; background: #f5f7fa; color: #909399; font-size: 12px;">
                    <span>加载失败</span>
                  </div>
                </template>
              </el-image>
            </div>
            <span v-else class="qrcode-placeholder">-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template #default="scope">
            <el-button
              v-if="scope.row.status === 'active'"
              size="small"
              type="warning"
              @click="quickToggleStatus(scope.row, 'disabled')"
              :loading="statusToggling[scope.row.id]"
            >
              禁用
            </el-button>
            <el-button
              v-if="scope.row.status === 'inactive' || scope.row.status === 'disabled'"
              size="small"
              type="success"
              @click="quickToggleStatus(scope.row, 'active')"
              :loading="statusToggling[scope.row.id]"
            >
              启用
            </el-button>
            <el-button 
              v-if="canEditDevice(scope.row)"
              size="small"
              type="primary"
              @click="onEdit(scope.row)"
            >编辑</el-button>
            <el-button size="small" type="primary" @click="onViewAccess(scope.row)">权限</el-button>
            <el-button 
              v-if="canEditDevice(scope.row)"
              size="small"
              type="primary"
              @click="onSetDefaultEmotion(scope.row)"
            >表情</el-button>
          <el-button
            v-if="canViewQrcode(scope.row)"
            size="small"
            type="primary"
            @click="onViewQrcode(scope.row)"
            :loading="qrcodeLoading[scope.row.id]"
          >
            <el-icon><QrCode /></el-icon>
            二维码
          </el-button>
            <el-button
              v-if="canDeleteDevice()"
              size="small"
              type="danger"
              @click="onDelete(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        style="margin-top: 20px; text-align: right;"
        background
        layout="prev, pager, next, jumper, ->, total"
        :total="total"
        :page-size="pageSize"
        v-model:current-page="currentPage"
        @current-change="handlePageChange"
      />
    </template>

    <!-- 普通用户视图：左侧设备卡片 + 右侧当前设备详情 -->
    <template v-else>
      <el-row :gutter="20" class="user-device-layout">
        <!-- 左侧：我的设备列表 -->
        <el-col :span="6">
          <el-card
            v-for="device in devices"
            :key="device.id"
            class="device-card"
            :class="{ active: currentDevice && currentDevice.id === device.id }"
            @click="selectDevice(device)"
          >
            <div class="device-card-name">{{ device.name || device.device_id }}</div>
            <div class="device-card-id">{{ device.device_id }}</div>
            <div class="device-card-type">
              <el-tag size="small" type="primary" v-if="device.board_type === '1'">{{ getBoardTypeName('1') }}</el-tag>
              <el-tag size="small" type="success" v-else-if="device.board_type === '2'">{{ getBoardTypeName('2') }}</el-tag>
              <el-tag size="small" type="warning" v-else-if="device.board_type === '3'">{{ getBoardTypeName('3') }}</el-tag>
              <el-tag size="small" v-else>{{ getBoardTypeName(device.board_type) }}</el-tag>
            </div>
            <div class="device-card-status">
              <el-tag size="small" type="success" v-if="device.status === 'active'">已激活</el-tag>
              <el-tag size="small" type="info" v-else-if="device.status === 'inactive'">未激活</el-tag>
              <el-tag size="small" type="warning" v-else-if="device.status === 'disabled'">已禁用</el-tag>
              <el-tag size="small" type="danger" v-else-if="device.status === 'deleted'">已删除</el-tag>
              <el-tag size="small" v-else>{{ device.status || '-' }}</el-tag>
            </div>
          </el-card>
        </el-col>

        <!-- 右侧：当前设备详情 + 简单操作 -->
        <el-col :span="18">
          <el-card v-if="currentDevice" class="device-detail-card">
            <template #header>
              <div class="device-detail-header">
                <div>
                  <div class="device-detail-name">{{ currentDevice.name || currentDevice.device_id }}</div>
                  <div class="device-detail-sub">
                    <span class="device-detail-id">ID：{{ currentDevice.device_id }}</span>
                    <span
                      v-if="currentDevice.board_type"
                      class="device-detail-type"
                    >
                      · 类型：
                      <span>{{ getBoardTypeName(currentDevice.board_type) }}</span>
                    </span>
                  </div>
                </div>
                <div class="device-detail-actions">
                  <el-button size="small" type="primary" @click="onEdit(currentDevice)">编辑设备</el-button>
                  <el-button
                    v-if="canViewQrcode(currentDevice)"
                    size="small"
                    type="primary"
                    @click="onViewQrcode(currentDevice)"
                    :loading="qrcodeLoading[currentDevice.id]"
                  >
                    <el-icon><QrCode /></el-icon>
                    二维码
                  </el-button>
                </div>
              </div>
            </template>

            <el-tabs v-model="activeUserTab" @tab-click="handleUserTabClick">
              <el-tab-pane label="设备配置" name="config">
                <el-descriptions :column="1" border>
                  <el-descriptions-item label="设备名称">{{ currentDevice.name || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="设备ID">{{ currentDevice.device_id }}</el-descriptions-item>
                  <el-descriptions-item label="BLE标识">{{ currentDevice.ble_identity || '-' }}</el-descriptions-item>
                  <el-descriptions-item label="类型">
                    <span>{{ getBoardTypeName(currentDevice.board_type) }}</span>
                  </el-descriptions-item>
                  <el-descriptions-item label="状态">
                    <span v-if="currentDevice.status === 'active'">已激活</span>
                    <span v-else-if="currentDevice.status === 'inactive'">未激活</span>
                    <span v-else-if="currentDevice.status === 'disabled'">已禁用</span>
                    <span v-else-if="currentDevice.status === 'deleted'">已删除</span>
                    <span v-else>{{ currentDevice.status || '-' }}</span>
                  </el-descriptions-item>
                  <el-descriptions-item label="注册时间">{{ currentDevice.created_at || '-' }}</el-descriptions-item>
                  <el-descriptions-item v-if="isAdmin" label="管理员允许 OTA">
                    <el-tag :type="currentDevice.ota_admin_enabled !== false ? 'success' : 'info'">
                      {{ currentDevice.ota_admin_enabled !== false ? '是' : '否' }}
                    </el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="OTA">
                    <el-tag :type="currentDevice.ota_user_enabled !== false ? 'success' : 'info'">
                      {{ currentDevice.ota_user_enabled !== false ? '是' : '否' }}
                    </el-tag>
                  </el-descriptions-item>
                </el-descriptions>
                <p style="color:#909399;font-size:12px;margin-top:10px;">
                  如需修改设备名称、OTA 开关等信息，请点击右上角“编辑设备”按钮。
                </p>
              </el-tab-pane>

              <el-tab-pane label="音乐" name="playlist">
                <div class="music-tip">
                  <div class="music-tip-line1">
                    <span class="music-tip-icon">i</span>
                    <span>试试对设备说：</span>
                  </div>
                  <div class="music-tip-line2">
                    <span class="music-random-text">
                      <span class="music-cursor"></span>
                      <span>{{ currentVoiceHint }}</span>
                    </span>
                  </div>
                </div>
                <p style="color:#909399;font-size:12px;margin-bottom:10px;">
                  当前设备已授权的歌单如下；如需调整，请联系客服人员。
                </p>
                <div v-if="deviceAccess && deviceAccess.length">
                  <ul class="device-playlist-list">
                    <li
                      v-for="item in deviceAccess"
                      :key="item.id || item.playlist_id"
                      class="device-playlist-item"
                    >
                      <span class="device-playlist-dot">•</span>
                      <span class="device-playlist-name">{{ item.playlist_name || '未命名歌单' }}</span>
                    </li>
                  </ul>
                </div>
                <el-empty
                  v-else
                  description="当前设备尚未授权任何歌单"
                  :image-size="80"
                />
              </el-tab-pane>

              <el-tab-pane label="表情管理" name="emotion">
                <p style="color:#909399;font-size:12px;margin-bottom:10px;">
                  设置或查看该设备的默认表情包。
                </p>

                <div v-if="currentDefaultEmotion" class="emotion-info-block">
                  <el-descriptions :column="1" border>
                    <el-descriptions-item label="表情包名称">
                      {{ currentDefaultEmotion.emotion_name }}
                    </el-descriptions-item>
                    <el-descriptions-item label="版本号">
                      {{ currentDefaultEmotion.emotion_version }}
                    </el-descriptions-item>
                    <el-descriptions-item label="设置时间">
                      {{ currentDefaultEmotion.set_at }}
                    </el-descriptions-item>
                  </el-descriptions>
                  <div class="emotion-actions">
                    <el-button
                      size="small"
                      type="primary"
                      @click="onSetDefaultEmotion(currentDevice)"
                    >
                      更换默认表情
                    </el-button>
                    <el-button
                      size="small"
                      type="danger"
                      @click="handleRemoveDefaultEmotion"
                      :loading="removingDefaultEmotion"
                    >
                      取消默认表情
                    </el-button>
                  </div>
                </div>

                <div v-else class="emotion-empty-block">
                  <el-empty
                    description="当前设备尚未设置默认表情"
                    :image-size="80"
                  />
                  <el-button
                    size="small"
                    type="primary"
                    @click="onSetDefaultEmotion(currentDevice)"
                    style="margin-top: 10px;"
                  >
                    设置默认表情
                  </el-button>
                </div>
              </el-tab-pane>

              <el-tab-pane
                v-if="currentDevice.board_type === '2'"
                label="课表"
                name="schedule"
              >
                <p style="color:#909399;font-size:12px;margin-bottom:10px;">
                  下方展示的是当前台历设备的最新课程表图片。
                </p>
                <div class="schedule-image-wrapper">
                  <el-skeleton v-if="scheduleLoading" animated :rows="3" style="width: 260px;" />
                  <el-image
                    v-else-if="scheduleImageUrl"
                    :src="scheduleImageUrl"
                    :preview-src-list="[scheduleImageUrl]"
                    fit="contain"
                    style="width: 260px; height: 200px; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.08);"
                  >
                    <template #error>
                      <div style="width: 260px; height: 200px; display: flex; align-items: center; justify-content: center; background: #f5f7fa; color: #909399; font-size: 12px;">
                        课表图片加载失败
                      </div>
                    </template>
                  </el-image>
                  <el-empty
                    v-else
                    description="当前设备暂无课程表"
                    :image-size="80"
                  />
                </div>
              </el-tab-pane>
            </el-tabs>
          </el-card>

          <el-empty v-else description="暂无设备，请联系客服人员为您绑定设备" />
        </el-col>
      </el-row>
    </template>

    <!-- 新增/编辑设备弹窗 -->
    <el-dialog
      :title="editMode ? '编辑设备' : '注册设备'"
      v-model="showEditDialog"
      width="600px"
    >
      <el-form
        ref="deviceForm"
        :model="editForm"
        :rules="deviceRules"
        label-width="100px"
      >
        <el-form-item label="设备ID (设备mac)" prop="device_id">
          <el-input v-model="editForm.device_id" :disabled="editMode" placeholder="请输入设备MAC地址" />
        </el-form-item>
        <el-form-item label="设备名称" prop="name">
          <el-input v-model="editForm.name" placeholder="请输入设备名称" />
        </el-form-item>
        <el-form-item label="Client ID" prop="client_id">
          <el-input
            v-model="editForm.client_id"
            placeholder="用于生成 BLE 短码（建议填设备 UUID/UID）"
            maxlength="255"
            clearable
            show-word-limit
            @blur="normalizeClientIdField"
          />
        </el-form-item>
        <el-form-item label="硬件类型" prop="board_type">
          <el-select
            v-model="editForm.board_type"
            placeholder="请选择硬件类型"
            :disabled="!isAdmin"
          >
            <el-option 
              v-for="board in boardTypes" 
              :key="board.id" 
              :label="board.name" 
              :value="String(board.id)" 
            />
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="isAdmin"
          label="所属用户"
          prop="user_id"
        >
          <el-select
            v-model="editForm.user_id"
            placeholder="请选择或搜索用户（输入关键词）"
            filterable
            clearable
            remote
            :remote-method="remoteSearchUsers"
            :loading="userSearchLoading"
            style="width: 100%"
          >
            <el-option
              v-for="user in users"
              :key="user.id"
              :label="getUserDisplayName(user)"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="设备状态" v-if="isAdmin && editMode">
          <el-select v-model="editForm.status" placeholder="请选择设备状态">
            <el-option label="未激活" value="inactive" />
            <el-option label="已激活" value="active" />
            <el-option label="已禁用" value="disabled" />
            <el-option label="已删除" value="deleted" />
          </el-select>
        </el-form-item>
        <el-form-item label="管理员允许 OTA" v-if="isAdmin && editMode">
          <el-switch v-model="editForm.ota_admin_enabled" />
          <span class="form-tip">关闭后，该设备不会收到任何固件升级</span>
        </el-form-item>
        <el-form-item label="用户允许 OTA" v-if="editMode">
          <el-switch v-model="editForm.ota_user_enabled" />
          <span class="form-tip">用户可关闭以暂不升级本设备</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
      </template>
    </el-dialog>

    <!-- 设置默认表情弹窗 -->
    <el-dialog
      title="设置设备默认表情"
      v-model="showDefaultEmotionDialog"
      width="600px"
    >
      <div v-if="currentDevice">
        <p><strong>设备：</strong>{{ currentDevice.name }} ({{ currentDevice.device_id }})</p>
        <el-divider />
        
        <div v-if="currentDefaultEmotion" style="margin-bottom: 20px;">
          <h5>当前默认表情：</h5>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="表情包名称">{{ currentDefaultEmotion.emotion_name }}</el-descriptions-item>
            <el-descriptions-item label="版本号">{{ currentDefaultEmotion.emotion_version }}</el-descriptions-item>
            <el-descriptions-item label="设置时间">{{ currentDefaultEmotion.set_at }}</el-descriptions-item>
          </el-descriptions>
          <el-button 
            type="danger" 
            size="small" 
            style="margin-top: 10px;"
            @click="handleRemoveDefaultEmotion"
            :loading="removingDefaultEmotion"
          >
            取消默认表情
          </el-button>
        </div>
        
        <el-divider v-if="currentDefaultEmotion" />
        
        <h5>{{ currentDefaultEmotion ? '更换默认表情' : '选择默认表情' }}：</h5>
        <el-select
          v-model="selectedEmotionId"
          placeholder="请选择表情包"
          filterable
          clearable
          style="width: 100%"
          :loading="loadingEmotions"
        >
          <el-option
            v-for="emotion in availableEmotions"
            :key="emotion.id"
            :label="`${emotion.name} (${emotion.version})`"
            :value="emotion.id"
          />
        </el-select>
        <p style="color: #909399; font-size: 12px; margin-top: 10px;">
          只能选择自己拥有的表情包（自己上传的或管理员授权的）
        </p>
      </div>
      <template #footer>
        <el-button @click="showDefaultEmotionDialog = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="handleSetDefaultEmotion"
          :disabled="!selectedEmotionId"
          :loading="settingDefaultEmotion"
        >
          确定
        </el-button>
      </template>
    </el-dialog>

    <!-- 设备权限管理弹窗 -->
    <el-dialog
      title="设备权限管理"
      v-model="showAccessDialog"
      width="800px"
    >
      <div v-if="currentDevice">
        <h4>设备信息：{{ currentDevice.name }} ({{ currentDevice.device_id }})</h4>
        
        <el-divider />
        
        <div class="access-section">
          <h5>已授权歌单</h5>
          <el-table :data="deviceAccess" style="width: 100%">
            <el-table-column prop="playlist_name" label="歌单名称" />
            <el-table-column label="操作" width="120" v-if="isAdmin">
              <template #default="scope">
                <el-button 
                  size="small" 
                  type="danger" 
                  @click="revokeAccess(scope.row)"
                >
                  撤销权限
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        
        <el-divider v-if="isAdmin" />
        
        <div class="access-section" v-if="isAdmin">
          <h5>授权新歌单</h5>
          <el-row :gutter="20">
            <el-col :span="16">
              <el-select
                v-model="newAccessPlaylist"
                placeholder="选择要授权的歌单"
                filterable
                clearable
                style="width: 100%"
              >
                <el-option
                  v-for="playlist in filteredAvailablePlaylists"
                  :key="playlist.id"
                  :label="playlist.name"
                  :value="playlist.id"
                />
              </el-select>
            </el-col>
            <el-col :span="8">
              <el-button 
                type="primary" 
                @click="grantAccess"
                :disabled="!newAccessPlaylist"
              >
                授权
              </el-button>
            </el-col>
          </el-row>
        </div>
      </div>
      <template #footer>
        <el-button @click="showAccessDialog = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 二维码预览对话框 -->
    <el-dialog
      v-model="showQrcodeDialog"
      :title="currentQrcodeDevice ? `设备二维码 - ${currentQrcodeDevice.name || currentQrcodeDevice.device_id}` : '设备二维码'"
      width="400px"
      align-center
    >
      <div v-if="currentQrcodeUrl" class="qrcode-preview">
        <el-image
          :src="getQrcodeImageUrl(currentQrcodeUrl)"
          fit="contain"
          :preview-src-list="[]"
          :preview-disabled="true"
          style="width: 100%; max-width: 300px; margin: 0 auto; display: block;"
        />
        <div style="text-align: center; margin-top: 20px;">
          <p style="color: #666; font-size: 14px; margin-bottom: 10px;">
            扫描二维码可绑定设备
          </p>
          <div style="display: flex; gap: 10px; justify-content: center;">
            <el-button
              type="primary"
              @click="downloadQrcode(currentQrcodeDevice)"
              :icon="Download"
            >
              下载二维码
            </el-button>
            <el-button
              type="warning"
              @click="regenerateQrcode(currentQrcodeDevice)"
              :loading="qrcodeLoading[currentQrcodeDevice?.id]"
            >
              重新生成
            </el-button>
          </div>
        </div>
      </div>
      <div v-else style="text-align: center; padding: 40px;">
        <el-icon class="is-loading" style="font-size: 40px;"><Loading /></el-icon>
        <p style="margin-top: 20px; color: #999;">正在生成二维码...</p>
      </div>
      <template #footer>
        <el-button @click="showQrcodeDialog = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Download, QrCode, Loading } from '@element-plus/icons-vue';
import { registerDevice, updateDevice, deleteDevice, getDevices, importDevices, getDeviceQrcode, getBoardTypes } from '../api/device';
import { getUsers } from '../api/auth';
import { getPlaylists } from '../api/playlist';
import { getPlaylistDeviceAccess, grantPlaylistAccess, revokePlaylistAccess } from '../api/playlist';
import { 
  getPacksForDeviceDefault,
  setDeviceDefaultEmotion, 
  getDeviceDefaultEmotion, 
  removeDeviceDefaultEmotion 
} from '../api/emotion';
import { getLatestDeviceSchedule } from '../api/deviceSchedule';

export default {
  name: 'DeviceManagement',
  setup() {
    const devices = ref([]);
    const users = ref([]);
    const playlists = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const searchQuery = ref('');
    const statusFilter = ref('');  // 状态筛选
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const importing = ref(false);
    const statusToggling = reactive({});  // 跟踪每个设备的状态切换加载状态
    const qrcodeLoading = reactive({});  // 跟踪每个设备的二维码加载状态
    const showEditDialog = ref(false);
    const showAccessDialog = ref(false);
    const showDefaultEmotionDialog = ref(false);
    const showQrcodeDialog = ref(false);
    const currentQrcodeUrl = ref('');
    const currentQrcodeDevice = ref(null);
    const editMode = ref(false);
    const currentDevice = ref(null);
    const deviceAccess = ref([]);
    const availablePlaylists = ref([]);
    const newAccessPlaylist = ref(null);
    const availableEmotions = ref([]);
    const selectedEmotionId = ref(null);
    const currentDefaultEmotion = ref(null);
    const loadingEmotions = ref(false);
    const settingDefaultEmotion = ref(false);
    const removingDefaultEmotion = ref(false);
    const scheduleImageUrl = ref('');
    const scheduleLoading = ref(false);
    const boardTypes = ref([]);
    // 语音提示文案（音乐Tab）
    const voiceHints = [
      '播放周杰伦的《稻香》',
      '播放数鸭子',
      '调小一点音量'
    ];
    const currentVoiceHint = ref('');
    const currentVoiceIndex = ref(0);
    
    // 当前用户信息
    const currentUser = ref({
      username: '',
      role: '',
      id: null
    });

    // 普通用户视图：当前选中设备与Tab
    const activeUserTab = ref('config');

    const editForm = reactive({
      id: null,
      device_id: '',
      name: '',
      client_id: '',
      board_type: '1',  // 默认值为 1（次元球）
      user_id: null,
      status: 'inactive',
      ota_admin_enabled: true,
      ota_user_enabled: true
    });

    const deviceRules = {
      device_id: [
        { required: true, message: '请输入设备ID', trigger: 'blur' }
      ],
      name: [
        { required: true, message: '请输入设备名称', trigger: 'blur' }
      ],
      client_id: [
        {
          validator: (rule, value, callback) => {
            const v = (value || '').trim();
            if (!v) {
              callback(new Error('请输入 Client ID'));
              return;
            }
            if (v.length > 255) {
              callback(new Error('最多 255 个字符'));
            } else {
              callback();
            }
          },
          trigger: 'blur'
        }
      ]
    };

    const deviceForm = ref();
    const importFileInput = ref(null);
    
    // 权限检查方法
    const canEditDevice = (device) => {
      // 管理员可以编辑所有设备
      if (currentUser.value.role === 'admin') {
        return true;
      }
      // 普通用户只能编辑自己的设备
      return device.user_id === currentUser.value.id;
    };
    
    const canDeleteDevice = () => {
      // 只有管理员可以删除设备（后端也做了限制）
      return currentUser.value.role === 'admin';
    };
    
    // 获取当前用户信息
    const getCurrentUser = () => {
      let user = {};
      try {
        const userStr = localStorage.getItem('user');
        if (userStr && userStr !== 'undefined') {
          user = JSON.parse(userStr);
        }
      } catch (e) {
        user = {};
      }
      if (!user.id) {
        ElMessage.error('用户信息异常，请重新登录');
        window.location.href = '/login';
        return;
      }
      currentUser.value = {
        username: user.username,
        role: user.role,
        id: user.id
      };
    };
    
    // 计算属性：是否为管理员
    const isAdmin = computed(() => currentUser.value.role === 'admin');
    
    // 过滤掉已授权的歌单
    const filteredAvailablePlaylists = computed(() => {
      if (!deviceAccess.value || deviceAccess.value.length === 0) {
        return availablePlaylists.value;
      }
      const authorizedPlaylistIds = new Set(
        deviceAccess.value.map(access => access.playlist_id)
      );
      return availablePlaylists.value.filter(
        playlist => !authorizedPlaylistIds.has(playlist.id)
      );
    });

    const loadDevices = async () => {
      try {
        loading.value = true;
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        };
        if (searchQuery.value) {
          params.query = searchQuery.value;
        }
        if (statusFilter.value && isAdmin.value) {
          params.status = statusFilter.value;
        }
        
        const response = await getDevices(params);
        devices.value = response.data || [];
        total.value = response.total || 0;
        
        // 确保每个设备都有 qrcodeUrl 属性（从 qrcode_url 复制）
        devices.value.forEach(device => {
          if (device.qrcode_url && !device.qrcodeUrl) {
            device.qrcodeUrl = device.qrcode_url;
          }
        });

        // 普通用户：默认选中第一台设备，或保持当前选择
        if (!isAdmin.value) {
          if (devices.value.length === 0) {
            currentDevice.value = null;
          } else if (!currentDevice.value || !devices.value.find(d => d.id === currentDevice.value.id)) {
            currentDevice.value = devices.value[0];
          }
        }
        
        // 加载设备二维码（异步，不阻塞列表显示）
        loadDevicesQrcodes();
      } catch (error) {
        console.error('加载设备列表失败:', error);
        ElMessage.error('加载设备列表失败');
      } finally {
        loading.value = false;
      }
    };

    // 加载设备二维码
    const loadDevicesQrcodes = async () => {
      // 为每个设备设置二维码URL（如果已有）
      devices.value.forEach((device) => {
        // 如果已有 qrcode_url（从数据库），直接使用
        if (device.qrcode_url && !device.qrcodeUrl) {
          // 直接设置，Vue 的响应式会自动更新视图
          device.qrcodeUrl = device.qrcode_url;
        }
        // 如果没有二维码URL，不自动生成（避免频繁调用API）
        // 只在用户点击"二维码"按钮时才生成
      });
    };

    // 普通用户视图：选择设备（只切换右侧视图，不弹出对话框）
    const selectDevice = async (device) => {
      currentDevice.value = device;
      activeUserTab.value = 'config';

      // 静默加载当前设备已授权歌单，用于“音乐/歌单权限”Tab 展示
      try {
        const response = await getPlaylistDeviceAccess(device.device_id);
        if (response.success) {
          deviceAccess.value = response.data || [];
        } else {
          // 普通切换不打断用户，不弹错误，只在控制台记录
          // eslint-disable-next-line no-console
          console.error('加载设备歌单权限失败:', response.message);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载设备歌单权限失败:', error);
      }

      // 静默加载默认表情信息，用于“表情管理”Tab 展示
      if (device && device.id) {
        try {
          await loadDeviceDefaultEmotion(device.id);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('加载设备默认表情失败:', e);
        }
      } else {
        currentDefaultEmotion.value = null;
      }

      // 重置并加载课表图片（仅台历设备）
      scheduleImageUrl.value = '';
      if (device && device.board_type === '2') {
        scheduleLoading.value = true;
        try {
          const resp = await getLatestDeviceSchedule(device.device_id);
          if (resp.success && resp.data && resp.data.final_image_url) {
            scheduleImageUrl.value = resp.data.final_image_url;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('加载设备课表图片失败:', e);
        } finally {
          scheduleLoading.value = false;
        }
      } else {
        scheduleLoading.value = false;
      }
    };

    // 普通用户视图：Tab 切换时按需加载数据
    const handleUserTabClick = async (tab) => {
      if (!currentDevice.value) return;

      // 切到“表情管理”时，如未加载过默认表情，则自动拉取一次
      if (tab.paneName === 'emotion' && !currentDefaultEmotion.value) {
        try {
          await loadDeviceDefaultEmotion(currentDevice.value.id);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('加载设备默认表情失败:', e);
        }
      }
    };

    // 生成设备二维码
    const generateDeviceQrcode = async (device, showDialog = true, force = false) => {
      if (!canViewQrcode(device)) {
        ElMessage.warning('您没有权限查看该设备的二维码');
        return;
      }

      // 如果设备已有二维码URL且不强制重新生成，直接使用
      if (device.qrcode_url && !force) {
        const deviceIndex = devices.value.findIndex(d => d.id === device.id);
        if (deviceIndex !== -1) {
          devices.value[deviceIndex].qrcodeUrl = device.qrcode_url;
        }
        if (showDialog) {
          currentQrcodeUrl.value = device.qrcode_url;
          currentQrcodeDevice.value = device;
          showQrcodeDialog.value = true;
        }
        return;
      }

      // 优先使用数据库ID，如果没有则使用MAC地址
      const deviceId = device.id || device.device_id;
      if (!deviceId) {
        ElMessage.error('设备ID不存在');
        return;
      }

      try {
        qrcodeLoading[device.id] = true;
        const params = force ? { force: 'true' } : {};
        const response = await getDeviceQrcode(deviceId, params);
        
        let qrcodeUrl = null;
        
        // 检查响应类型：可能是JSON（包含URL）或blob（图片数据）
        if (response && typeof response === 'object' && response.url && !(response instanceof Blob)) {
          // 返回的是JSON格式，包含URL
          qrcodeUrl = response.url;
          
          // 更新设备对象的二维码URL（保存到数据库的URL）
          const deviceIndex = devices.value.findIndex(d => d.id === device.id);
          if (deviceIndex !== -1) {
            devices.value[deviceIndex].qrcodeUrl = qrcodeUrl;
            devices.value[deviceIndex].qrcode_url = qrcodeUrl; // 同时更新数据库字段
          }
        } else if (response && response.data instanceof Blob) {
          // 返回的是blob（七牛云未配置时）
          const blob = response.data;
          qrcodeUrl = URL.createObjectURL(blob);
          
          // 更新设备对象的二维码URL
          const deviceIndex = devices.value.findIndex(d => d.id === device.id);
          if (deviceIndex !== -1) {
            devices.value[deviceIndex].qrcodeUrl = qrcodeUrl;
          }
        } else {
          throw new Error('无效的响应格式');
        }
        
        if (showDialog && qrcodeUrl) {
          currentQrcodeUrl.value = qrcodeUrl;
          currentQrcodeDevice.value = device;
          showQrcodeDialog.value = true;
          
          // 如果是强制重新生成，显示成功消息
          if (force) {
            ElMessage.success('二维码已重新生成');
          }
        }
        
        // 如果是强制重新生成，刷新设备列表以确保数据同步
        if (force && qrcodeUrl) {
          // 延迟刷新，确保后端数据库已更新
          setTimeout(() => {
            loadDevices();
          }, 500);
        }
      } catch (error) {
        console.error('生成二维码失败:', error);
        const errorMsg = error.response?.data?.message || error.message || '生成二维码失败，请稍后重试';
        ElMessage.error(errorMsg);
      } finally {
        qrcodeLoading[device.id] = false;
      }
    };

    // 查看二维码
    const onViewQrcode = async (device) => {
      // 如果已有二维码URL（从数据库或已生成），直接显示
      if (device.qrcodeUrl || device.qrcode_url) {
        currentQrcodeUrl.value = device.qrcodeUrl || device.qrcode_url;
        currentQrcodeDevice.value = device;
        showQrcodeDialog.value = true;
      } else {
        // 否则生成新的二维码
        await generateDeviceQrcode(device, true);
      }
    };

    // 重新生成二维码
    const regenerateQrcode = async (device) => {
      if (!device) {
        ElMessage.warning('设备信息不存在');
        return;
      }
      
      // 使用 force=true 参数强制重新生成
      await generateDeviceQrcode(device, true, true);
    };

    // 下载二维码
    const downloadQrcode = async (device) => {
      const qrcodeUrl = device.qrcodeUrl || device.qrcode_url || currentQrcodeUrl.value;
      if (!qrcodeUrl) {
        ElMessage.warning('二维码未生成，请先查看二维码');
        return;
      }

      try {
        // 从URL获取blob
        const response = await fetch(qrcodeUrl);
        const blob = await response.blob();
        
        // 生成文件名：优先使用设备名称，如果为空则使用 device_id 或 id
        let filename = 'qrcode';
        if (device.name && device.name.trim()) {
          // 清理设备名称中的特殊字符，只保留字母、数字、中文、下划线和连字符
          filename = device.name.trim().replace(/[/\\:*?"<>|]/g, '_');
        } else if (device.device_id) {
          filename = device.device_id;
        } else if (device.id) {
          filename = `device_${device.id}`;
        }
        filename = `${filename}.png`;
        
        // 创建下载链接
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        ElMessage.success('二维码下载成功');
      } catch (error) {
        console.error('下载二维码失败:', error);
        ElMessage.error('下载二维码失败');
      }
    };

    // 检查是否可以查看二维码
    const canViewQrcode = (device) => {
      // 管理员可以查看所有设备的二维码
      if (isAdmin.value) {
        return true;
      }
      // 普通用户只能查看自己设备的二维码
      return device.user_id === currentUser.value.id;
    };

    // 处理二维码图片加载错误
    const handleQrcodeImageError = (e) => {
      const imgElement = e.target;
      const src = imgElement?.src || 'unknown';
      
      // 获取原始URL（从设备数据中）
      const deviceRow = devices.value.find(d => {
        const qrcodeUrl = d.qrcodeUrl || d.qrcode_url;
        if (!qrcodeUrl) return false;
        // 比较URL（去除可能的查询参数）
        const srcBase = src.split('?')[0].split('#')[0];
        const qrcodeBase = qrcodeUrl.split('?')[0].split('#')[0];
        return srcBase === qrcodeBase || src.includes(qrcodeBase) || qrcodeBase.includes(srcBase);
      });
      const originalUrl = deviceRow ? (deviceRow.qrcodeUrl || deviceRow.qrcode_url) : null;
      
      // 静默处理，不输出错误日志（避免控制台噪音）
      // 只在开发环境或需要调试时输出
      if (process.env.NODE_ENV === 'development') {
        console.warn('二维码图片加载失败:', {
          '当前使用的URL': src,
          '原始URL': originalUrl,
          '错误类型': e.type
        });
      }
      
      // 可以在这里添加重试逻辑或显示占位图
      // 但为了不影响用户体验，暂时静默处理
    };

    // 获取二维码图片URL（处理中文字符等特殊字符）
    const getQrcodeImageUrl = (url) => {
      if (!url) return '';
      
      // 检查URL是否包含未编码的中文字符
      const hasUnencodedChinese = /[\u4e00-\u9fff]/.test(url);
      
      if (!hasUnencodedChinese) {
        // URL不包含中文字符，直接返回（新生成的URL都是这种格式）
        return url;
      }
      
      // URL包含中文字符（旧数据），需要编码
      try {
        if (url.startsWith('http://') || url.startsWith('https://')) {
          // 使用URL构造函数会自动处理编码
          const urlObj = new URL(url);
          return urlObj.href;
        }
        return url;
      } catch (e) {
        // 如果URL解析失败，手动编码路径部分
        try {
          const match = url.match(/^(https?:\/\/[^/]+)(\/.*)$/);
          if (match) {
            const [, base, path] = match;
            // 编码路径部分
            const encodedPath = path.split('/').map(segment => {
              try {
                // 先解码再编码，避免重复编码
                return encodeURIComponent(decodeURIComponent(segment));
              } catch {
                return encodeURIComponent(segment);
              }
            }).join('/');
            return base + encodedPath;
          }
        } catch (e2) {
          console.warn('URL编码失败，使用原始URL:', url, e2);
        }
        return url;
      }
    };

    const handleStatusFilterChange = () => {
      currentPage.value = 1;  // 重置到第一页
      loadDevices();
    };

    const userSearchLoading = ref(false);
    const remoteSearchUsers = async (query) => {
      userSearchLoading.value = true;
      try {
        const response = await getUsers({
          query: (query || '').trim(),
          page: 1,
          page_size: 50
        });
        users.value = response.data || [];
      } catch (error) {
        console.error('搜索用户失败:', error);
        users.value = [];
      } finally {
        userSearchLoading.value = false;
      }
    };

    // 获取用户显示名称
    const getUserDisplayName = (user) => {
      // 微信用户：显示昵称或"微信用户"
      if (user.openid) {
        const nickname = user.nickname || '微信用户';
        return `${nickname} (ID: ${user.id})`;
      }
      // 普通用户：显示用户名
      const username = user.username || `用户${user.id}`;
      return `${username} (ID: ${user.id})`;
    };

    const loadPlaylists = async () => {
      try {
        const response = await getPlaylists();
        playlists.value = response.data || [];
        availablePlaylists.value = response.data || [];
      } catch (error) {
        console.error('加载歌单列表失败:', error);
      }
    };

    const normalizeClientIdField = () => {
      if (!editForm.client_id) return;
      editForm.client_id = editForm.client_id.trim();
    };

    const handleSearch = () => {
      currentPage.value = 1;
      loadDevices();
    };

    const handlePageChange = (page) => {
      currentPage.value = page;
      loadDevices();
    };

    const onEdit = async (device) => {
      // 检查编辑权限
      if (!canEditDevice(device)) {
        ElMessage.warning('您没有权限编辑此设备');
        return;
      }
      
      editMode.value = true;
      Object.assign(editForm, {
        id: device.id,
        device_id: device.device_id,
        name: device.name,
        client_id: device.client_id || '',
        // 统一为字符串，方便与 <el-option :value=\"String(board.id)\"> 匹配，显示为 name 而不是数字
        board_type: device.board_type != null ? String(device.board_type) : '1',
        user_id: device.user_id,
        status: device.status || 'inactive',
        ota_admin_enabled: device.ota_admin_enabled !== undefined ? !!device.ota_admin_enabled : true,
        ota_user_enabled: device.ota_user_enabled !== undefined ? !!device.ota_user_enabled : true
      });
      showEditDialog.value = true;
      // 打开弹窗时加载用户列表（支持搜索），并保证当前已选用户能显示
      if (isAdmin.value) {
        await remoteSearchUsers('');
        if (device.user_id && !users.value.some(u => u.id === device.user_id)) {
          users.value.unshift({
            id: device.user_id,
            username: device.user_name || '',
            nickname: '',
            openid: null
          });
        }
      }
    };

    const onDelete = async (device) => {
      // 检查删除权限
      if (!canDeleteDevice(device)) {
        ElMessage.warning('您没有权限删除此设备');
        return;
      }
      
      try {
        await ElMessageBox.confirm(`确定要删除设备"${device.name}"吗？`, '提示', {
          type: 'warning',
        });
        
        const response = await deleteDevice(device.id);
        if (response.success) {
          ElMessage.success('删除成功');
          loadDevices();
        } else {
          ElMessage.error(response.message || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除设备失败:', error);
          ElMessage.error('删除设备失败');
        }
      }
    };

    const handleSave = async () => {
      try {
        const isEdit = !!editForm.id;

        // 非管理员不允许新建设备（防止误用）
        if (!isAdmin.value && !isEdit) {
          ElMessage.error('只有管理员可以创建设备');
          return;
        }

        // const saveFn = isEdit ? updateDevice : registerDevice;

        // 非管理员编辑时，后端会自动限制不能修改 user_id/status，这里仅保证 user_id 不为空
        if (!isAdmin.value) {
          editForm.user_id = currentUser.value.id;
        }
        await deviceForm.value.validate();
        saving.value = true;
        const payload = { ...editForm };
        normalizeClientIdField();
        payload.client_id = (editForm.client_id || '').trim();

        // 普通用户不能修改状态，避免误传
        if (!isAdmin.value) {
          delete payload.status;
        } else if (!isEdit) {
          // 创建新设备时：如果 status 是默认值 'inactive'，不传递 status 字段
          // 让后端根据 user_id 自动判断（有用户 -> active，无用户 -> inactive）
          if (payload.status === 'inactive') {
            delete payload.status;
          }
        }
        let response = null;
        if (isEdit) {
          const updatePayload = { ...payload };
          delete updatePayload.id;
          response = await updateDevice(editForm.id, updatePayload);
        } else {
          const createPayload = { ...payload };
          delete createPayload.id;
          response = await registerDevice(createPayload);
        }
        // const response = await saveFn(editForm.id, payload);
        if (response?.success) {
          ElMessage.success(editMode.value ? '更新成功' : '创建成功');
          showEditDialog.value = false;
          await loadDevices();
          
          // 如果是新建设备，生成二维码并显示
          if (!editMode.value) {
            // 等待设备列表加载完成后再生成二维码
            setTimeout(async () => {
              // 查找刚创建的设备（通过 device_id 匹配）
              const device = devices.value.find(d => d.device_id === editForm.device_id);
              if (device && canViewQrcode(device)) {
                await generateDeviceQrcode(device, true); // 显示对话框
              }
            }, 500);
          }
        } else {
          ElMessage.error(response.message || (editMode.value ? '更新失败' : '创建失败'));
        }
      } catch (error) {
        console.error('保存设备失败:', error);
        ElMessage.error('保存设备失败');
      } finally {
        saving.value = false;
      }
    };

    const resetForm = () => {
      if (!currentUser.value.id) {
        ElMessage.error('用户信息异常，请重新登录');
        return;
      }
      editMode.value = false;
      Object.assign(editForm, {
        id: null,
        device_id: '',
        name: '',
        client_id: '',
        board_type: '1',  // 默认值为 1（次元球）
        user_id: isAdmin.value ? null : currentUser.value.id,
        status: 'inactive',
        ota_admin_enabled: true,
        ota_user_enabled: true
      });
      deviceForm.value?.resetFields();
    };

    const triggerImport = () => {
      if (importFileInput.value) {
        importFileInput.value.value = '';
        importFileInput.value.click();
      }
    };

    const downloadTemplate = () => {
      // CSV 模板内容
      const csvContent = `device_id,name,board_type,client_id,user_id
58:8c:81:2b:2e:c4,示例设备1-次元球,1,41503448323936040040854256001078,
aa:bb:cc:dd:ee:ff,示例设备2-台历,2,,
11:22:33:44:55:66,示例设备3-吧唧,3,,
ff:ee:dd:cc:bb:aa,示例设备4-次元球（已指定用户）,1,,1

说明：
- device_id: 必填，设备MAC地址（如：58:8c:81:2b:2e:c4）
- name: 必填，设备名称
- board_type: 可选，类型（1-次元球，2-台历，3-吧唧），默认为1
- client_id: 可选，设备 UUID/UID（后端会自动生成 BLE 短码）
- user_id: 可选，所属用户ID，默认为空（未分配）`;

      // 创建 Blob 对象
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // 添加 BOM 以支持 Excel 正确识别 UTF-8
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '设备批量导入模板.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      ElMessage.success('模板下载成功');
    };

    // 快速切换设备状态（仅管理员）
    const quickToggleStatus = async (device, newStatus) => {
      if (!isAdmin.value) {
        ElMessage.warning('只有管理员可以修改设备状态');
        return;
      }

      const statusText = {
        'active': '启用',
        'disabled': '禁用'
      }[newStatus] || newStatus;

      try {
        statusToggling[device.id] = true;
        const response = await updateDevice(device.id, {
          user_id: device.user_id,
          device_id: device.device_id,
          board_type: device.board_type || '1',
          name: device.name,
          status: newStatus
        });

        if (response.success) {
          ElMessage.success(`设备已${statusText}`);
          // 刷新设备列表
          loadDevices();
        } else {
          ElMessage.error(response.message || `${statusText}失败`);
        }
      } catch (error) {
        console.error('切换设备状态失败:', error);
        ElMessage.error(`${statusText}失败，请检查网络连接`);
      } finally {
        statusToggling[device.id] = false;
      }
    };

    const handleFileChange = async (event) => {
      const file = event.target.files && event.target.files[0];
      if (!file) {
        return;
      }

      const fileName = file.name || '';
      if (!fileName.toLowerCase().endsWith('.csv')) {
        ElMessage.error('目前仅支持导入 CSV 文件');
        event.target.value = '';
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        importing.value = true;
        const response = await importDevices(formData);
        if (response.success) {
          const summary = response.summary || {};
          const msg = response.message || '导入完成';
          ElMessage.success({
            message: msg,
            duration: 5000,
            showClose: true
          });
          // 导入成功后刷新设备列表
          loadDevices();
          // 控制台打印详细错误行，便于排查
          if (summary.errors && summary.errors.length) {
            // eslint-disable-next-line no-console
            console.log('导入错误详情:', summary.errors);
          }
        } else {
          ElMessage.error(response.message || '导入失败');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('批量导入失败:', error);
        ElMessage.error('批量导入失败，请检查文件格式或网络连接');
      } finally {
        importing.value = false;
        event.target.value = '';
      }
    };

    const onViewAccess = async (device) => {
      currentDevice.value = device;
      newAccessPlaylist.value = null;
      
      try {
        const response = await getPlaylistDeviceAccess(device.device_id);
        if (response.success) {
          deviceAccess.value = response.data || [];
        } else {
          ElMessage.error(response.message || '获取设备权限失败');
        }
      } catch (error) {
        console.error('获取设备权限失败:', error);
        ElMessage.error('获取设备权限失败');
      }
      
      showAccessDialog.value = true;
    };

    // 设置默认表情相关方法
    const onSetDefaultEmotion = async (device) => {
      // 检查权限
      if (!canEditDevice(device)) {
        ElMessage.warning('您没有权限为该设备设置默认表情');
        return;
      }
      
      currentDevice.value = device;
      selectedEmotionId.value = null;
      currentDefaultEmotion.value = null;
      
      // 加载支持该设备版型的表情包列表（zip+bin，仅显示支持当前设备类型的包）
      await loadAvailableEmotions(device.id);
      
      // 加载当前设备的默认表情
      await loadDeviceDefaultEmotion(device.id);
      
      showDefaultEmotionDialog.value = true;
    };

    const loadAvailableEmotions = async (deviceId) => {
      if (!deviceId) return;
      try {
        loadingEmotions.value = true;
        const response = await getPacksForDeviceDefault(deviceId);
        if (response.success) {
          availableEmotions.value = response.data || [];
        } else {
          ElMessage.error(response.message || '加载表情包列表失败');
        }
      } catch (error) {
        console.error('加载表情包列表失败:', error);
        ElMessage.error('加载表情包列表失败');
      } finally {
        loadingEmotions.value = false;
      }
    };

    const loadDeviceDefaultEmotion = async (deviceId) => {
      try {
        const response = await getDeviceDefaultEmotion(deviceId);
        if (response.success) {
          const data = response.data || null;
          if (data) {
            // 兼容历史字段名：统一挂到 emotion_name / emotion_version，方便模板使用
            currentDefaultEmotion.value = {
              ...data,
              emotion_name: data.emotion_name || data.name || '',
              emotion_version: data.emotion_version || data.version || '',
            };
            selectedEmotionId.value = data.pack_id;
          } else {
            currentDefaultEmotion.value = null;
          }
        } else {
          // 如果没有设置默认表情，这是正常的
          currentDefaultEmotion.value = null;
        }
      } catch (error) {
        console.error('加载设备默认表情失败:', error);
        currentDefaultEmotion.value = null;
      }
    };

    const handleSetDefaultEmotion = async () => {
      if (!selectedEmotionId.value || !currentDevice.value) {
        return;
      }
      
      try {
        settingDefaultEmotion.value = true;
        const response = await setDeviceDefaultEmotion(currentDevice.value.id, selectedEmotionId.value);
        if (response.success) {
          ElMessage.success('设备默认表情设置成功');
          await loadDeviceDefaultEmotion(currentDevice.value.id);
        } else {
          ElMessage.error(response.message || '设置失败');
        }
      } catch (error) {
        console.error('设置设备默认表情失败:', error);
        ElMessage.error('设置设备默认表情失败');
      } finally {
        settingDefaultEmotion.value = false;
      }
    };

    const handleRemoveDefaultEmotion = async () => {
      if (!currentDevice.value) {
        return;
      }
      
      try {
        await ElMessageBox.confirm('确定要取消该设备的默认表情吗？', '提示', {
          type: 'warning',
        });
        
        removingDefaultEmotion.value = true;
        const response = await removeDeviceDefaultEmotion(currentDevice.value.id);
        if (response.success) {
          ElMessage.success('已取消设备默认表情');
          currentDefaultEmotion.value = null;
          selectedEmotionId.value = null;
        } else {
          ElMessage.error(response.message || '取消失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('取消设备默认表情失败:', error);
          ElMessage.error('取消设备默认表情失败');
        }
      } finally {
        removingDefaultEmotion.value = false;
      }
    };

    const grantAccess = async () => {
      if (!newAccessPlaylist.value) {
        ElMessage.warning('请选择要授权的歌单');
        return;
      }
      
      try {
        const response = await grantPlaylistAccess({
          device_id: currentDevice.value.device_id,
          playlist_id: newAccessPlaylist.value
        });
        
        if (response.success) {
          ElMessage.success('授权成功');
          newAccessPlaylist.value = null;
          // 重新加载设备权限
          const accessResponse = await getPlaylistDeviceAccess(currentDevice.value.device_id);
          if (accessResponse.success) {
            deviceAccess.value = accessResponse.data || [];
          }
        } else {
          ElMessage.error(response.message || '授权失败');
        }
      } catch (error) {
        console.error('授权失败:', error);
        ElMessage.error('授权失败');
      }
    };

    const revokeAccess = async (access) => {
      try {
        const response = await revokePlaylistAccess(access.id);
        
        if (response.success) {
          ElMessage.success('撤销权限成功');
          // 重新加载设备权限
          const accessResponse = await getPlaylistDeviceAccess(currentDevice.value.device_id);
          if (accessResponse.success) {
            deviceAccess.value = accessResponse.data || [];
          }
        } else {
          ElMessage.error(response.message || '撤销权限失败');
        }
      } catch (error) {
        console.error('撤销权限失败:', error);
        ElMessage.error('撤销权限失败');
      }
    };

    let voiceHintTimer = null;
    let voiceHintDelayTimer = null;

    const startVoiceHintTypewriter = () => {
      if (voiceHintTimer) {
        clearInterval(voiceHintTimer);
        voiceHintTimer = null;
      }
      if (voiceHintDelayTimer) {
        clearTimeout(voiceHintDelayTimer);
        voiceHintDelayTimer = null;
      }

      const text = voiceHints[currentVoiceIndex.value] || '';
      let i = 0;
      currentVoiceHint.value = '';

      voiceHintTimer = setInterval(() => {
        if (i < text.length) {
          currentVoiceHint.value += text[i];
          i += 1;
        } else {
          clearInterval(voiceHintTimer);
          voiceHintTimer = null;
          // 停顿一会儿再播放下一句
          voiceHintDelayTimer = setTimeout(() => {
            currentVoiceIndex.value = (currentVoiceIndex.value + 1) % voiceHints.length;
            startVoiceHintTypewriter();
          }, 1600);
        }
      }, 150);
    };

    const loadBoardTypes = async () => {
      try {
        const response = await getBoardTypes();
        if (response.success && response.data) {
          boardTypes.value = response.data;
        }
      } catch (error) {
        console.error('获取版型列表失败:', error);
      }
    };
    
    // 获取版型名称
    const getBoardTypeName = (typeId) => {
      if (!typeId) return '-';
      const board = boardTypes.value.find(b => String(b.id) === String(typeId));
      if (board) return board.name;
      // 未知或未配置的板型，普通/管理员用户都不直接展示数字 ID
      return '未知硬件类型';
    };

    onMounted(() => {
      getCurrentUser();
      // 等待currentUser获取后再resetForm
      setTimeout(() => {
        resetForm();
      }, 100);
      loadDevices();
      loadPlaylists();
      loadBoardTypes();
      // 启动语音提示打字效果
      startVoiceHintTypewriter();
    });

    onUnmounted(() => {
      if (voiceHintTimer) {
        clearInterval(voiceHintTimer);
        voiceHintTimer = null;
      }
      if (voiceHintDelayTimer) {
        clearTimeout(voiceHintDelayTimer);
        voiceHintDelayTimer = null;
      }
    });

    return {
      devices,
      users,
      userSearchLoading,
      remoteSearchUsers,
      playlists,
      loading,
      saving,
      searchQuery,
      currentPage,
      pageSize,
      total,
      showEditDialog,
      showAccessDialog,
      showQrcodeDialog,
      currentQrcodeUrl,
      currentQrcodeDevice,
      qrcodeLoading,
      editMode,
      currentDevice,
      deviceAccess,
      availablePlaylists,
      filteredAvailablePlaylists,
      newAccessPlaylist,
      currentUser,
      editForm,
      deviceRules,
      deviceForm,
      canEditDevice,
      canDeleteDevice,
      isAdmin,
      handleSearch,
      handlePageChange,
      normalizeClientIdField,
      onEdit,
      onDelete,
      handleSave,
      // 普通用户视图相关
      activeUserTab,
      selectDevice,
      handleUserTabClick,
      resetForm,
      triggerImport,
      handleFileChange,
      onViewAccess,
      grantAccess,
      onViewQrcode,
      regenerateQrcode,
      downloadQrcode,
      canViewQrcode,
      handleQrcodeImageError,
      getQrcodeImageUrl,
      revokeAccess,
      onSetDefaultEmotion,
      showDefaultEmotionDialog,
      availableEmotions,
      selectedEmotionId,
      currentDefaultEmotion,
      loadingEmotions,
      settingDefaultEmotion,
      removingDefaultEmotion,
      handleSetDefaultEmotion,
      handleRemoveDefaultEmotion,
      getUserDisplayName,
      importFileInput,
      importing,
      downloadTemplate,
      Download,
      QrCode,
      Loading,
      statusFilter,
      handleStatusFilterChange,
      quickToggleStatus,
      statusToggling,
      currentVoiceHint,
      scheduleImageUrl,
      scheduleLoading,
      boardTypes,
      getBoardTypeName
    };
  }
};
</script>

<style scoped>
.device-management {
  padding: 20px;
}

.access-section {
  margin-bottom: 20px;
}

.access-section h5 {
  margin-bottom: 10px;
  color: #333;
}

.qrcode-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qrcode-placeholder {
  color: #999;
  font-size: 12px;
}

.form-tip {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.qrcode-preview {
  text-align: center;
  padding: 20px 0;
}

/* 普通用户视图样式优化 */
.user-device-layout {
  margin-top: 10px;
}

.device-card {
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.device-card.active {
  border-color: #409EFF;
  box-shadow: 0 0 0 1px rgba(64, 158, 255, 0.3);
}

.device-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.device-card-id {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.device-card-type,
.device-card-status {
  font-size: 12px;
  margin-bottom: 2px;
}

.device-detail-card {
  min-height: 260px;
}

.device-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.device-detail-name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.device-detail-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.device-detail-sub .device-detail-type {
  margin-left: 6px;
}

.device-detail-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.device-playlist-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.device-playlist-item {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 13px;
  color: #606266;
}

.device-playlist-dot {
  display: inline-block;
  width: 12px;
  text-align: center;
  margin-right: 4px;
  color: #409EFF;
}

.device-playlist-name {
  flex: 1;
  word-break: break-all;
}

.emotion-info-block {
  margin-top: 4px;
}

.emotion-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}

.emotion-empty-block {
  margin-top: 4px;
  text-align: center;
}

.music-tip {
  text-align: center;
  margin-bottom: 12px;
}

.music-tip-line1 {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #909399;
}

.music-tip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid #d3d4d6;
  font-size: 12px;
  font-style: italic;
}

.music-tip-line2 {
  margin-top: 6px;
  height: 24px;
  width: 220px;
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
}

.music-random-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.music-cursor {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  background-color: #409EFF;
  animation: music-cursor-blink 1s steps(1, end) infinite;
}

.music-random-text span:last-child {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

@keyframes music-cursor-blink {
  0%, 50% {
    opacity: 1;
  }
  50.01%, 100% {
    opacity: 0;
  }
}

.schedule-image-wrapper {
  margin-top: 8px;
  display: flex;
  justify-content: center;
}
</style> 